import type { McpScope } from '@/api/types';
import { formatUtcDate } from '@/ui/format';

/** `read`: ticked at first. `internal`: GFT only content, ticked only on purpose: the AI tool's provider receives it.
 *  `change`: what the AI tool may change for the person (never publish, archive or create assets), also only on purpose. */
export type ScopeGroup = 'read' | 'internal' | 'change';

/** The keys a token may carry, in the order the dialog lists them. */
export const SCOPES: readonly { key: McpScope; label: string; hint: string; group: ScopeGroup }[] = [
  { key: 'idea.view', label: 'Read business opportunities', hint: 'Read opportunities and their research', group: 'read' },
  { key: 'asset.view', label: 'Read the asset catalog', hint: 'Search and read published assets, shareable content', group: 'read' },
  { key: 'industry.view', label: 'Read industries', hint: 'The industries that tag opportunities and assets', group: 'read' },
  {
    key: 'asset.view.internal',
    label: 'Read internal content',
    hint: "Internal sections, pricing and client names: your AI tool's provider receives them",
    group: 'internal',
  },
  { key: 'asset.view.draft', label: 'Read drafts and archived assets', hint: 'Assets not published yet, or retired', group: 'internal' },
  { key: 'industry.create', label: 'Add industries', hint: 'New approved industries', group: 'change' },
  { key: 'industry.suggest', label: 'Suggest industries', hint: 'Proposals that wait for review', group: 'change' },
  { key: 'industry.update', label: 'Edit industries', hint: 'Rename, describe, deactivate or reactivate', group: 'change' },
  { key: 'industry.review', label: 'Approve and reject industries', hint: 'A rejection moves everything to a replacement', group: 'change' },
  { key: 'asset.manage', label: 'Edit assets', hint: 'Fields, sections, relations and cases; a published asset changes at once', group: 'change' },
  { key: 'idea.triage', label: 'Triage opportunities', hint: 'Park, archive or reactivate', group: 'change' },
  { key: 'idea.industry.change', label: "Set an opportunity's industry", hint: 'Research does not run again', group: 'change' },
];

export const GROUP_LABELS: Record<ScopeGroup, string> = { read: 'Read', internal: 'GFT only', change: 'Change' };

/** The keys ticked when a dialog opens: the read group of those the person holds. */
export function defaultScopes(held: readonly { key: string; group: ScopeGroup }[]): string[] {
  return held.filter((scope) => scope.group === 'read').map((scope) => scope.key);
}

export function scopeLabel(key: string): string {
  return SCOPES.find((scope) => scope.key === key)?.label ?? key;
}

/** A token lasts at most this many days from today, and that is also the usual lifetime. */
export const DEFAULT_DAYS = 90;
export const MAX_DAYS = 90;

const DAY_MS = 86_400_000;

/** The UTC calendar day `days` after `from` (a UTC day or now), as YYYY-MM-DD: the server counts days in UTC, so the pickers do too. */
export function utcDay(days: number, from?: string): string {
  const base = from ? Date.parse(`${from}T00:00:00Z`) : Date.now();
  return new Date(base + days * DAY_MS).toISOString().slice(0, 10);
}

/** The first day a token may work through, and the last. */
export const earliestDay = () => utcDay(1);
export const latestDay = () => utcDay(MAX_DAYS);

/** The line under a date field once a date is picked. */
export function worksThrough(day: string): string {
  return `Works through ${formatUtcDate(`${day}T00:00:00Z`)}, end of day UTC.`;
}
